// A25 — Anti-fraud va risk. Module-local data layer (does NOT touch
// `lib/adapters.ts` / `i18n/*` — C-session HOT). Backend `/admin/antifraud/*`
// greenfield → fetchers fail-soft to empty; mutations POST when it ships.
// Registration = ./REGISTER.md (C applies routes/screenStatus/nav/i18n/perm).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

function toResult<T>(
  rows: T[],
  params: FetchParams,
  search?: (r: T) => string,
): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/** A25.07 — a suspicious event awaiting review. */
export interface SuspiciousEventRow {
  id: number
  user: string
  type: string
  score: number
  reason: string
  created_at: string
  status: 'open' | 'blocked' | 'cleared'
}

/** A25.02 — a fraud detection rule. */
export interface FraudRuleRow {
  id: number
  name: string
  condition: string
  action: 'flag' | 'block' | 'review'
  enabled: boolean
  hits: number
}

/** A25.15 — a blacklist entry. */
export interface BlacklistRow {
  id: number
  kind: 'ip' | 'device' | 'phone' | 'email' | 'card'
  value: string
  reason: string
  added_at: string
}

export function fetchEvents(params: FetchParams): Promise<FetchResult<SuspiciousEventRow>> {
  return safeList<SuspiciousEventRow>('/admin/antifraud/events', 'events', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.user} ${r.type} ${r.reason}`),
  )
}

/** POST /admin/antifraud/events/:id/:action — block|clear (A25.07). */
export function resolveEvent(id: number, action: 'block' | 'clear'): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/antifraud/events/${id}/${action}`, {})
}

export function fetchRules(params: FetchParams): Promise<FetchResult<FraudRuleRow>> {
  return safeList<FraudRuleRow>('/admin/antifraud/rules', 'rules', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.name} ${r.condition}`),
  )
}

/** POST /admin/antifraud/rules/:id/toggle {enabled} (A25.02). */
export function toggleRule(id: number, enabled: boolean): Promise<{ enabled: boolean }> {
  return api.post<{ enabled: boolean }>(`/admin/antifraud/rules/${id}/toggle`, { enabled })
}

export function fetchBlacklist(params: FetchParams): Promise<FetchResult<BlacklistRow>> {
  return safeList<BlacklistRow>('/admin/antifraud/blacklist', 'entries', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.value} ${r.reason}`),
  )
}

/** DELETE /admin/antifraud/blacklist/:id (A25.15). */
export function removeBlacklist(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/antifraud/blacklist/${id}`)
}

/** A25.09 — a device fingerprint with the accounts sharing it. */
export interface DeviceRow {
  id: string
  platform: string
  accounts: number
  first_seen: string
  last_seen: string
  flagged: boolean
}

/** A25.12 — a suspected fraudulent payment. */
export interface PaymentFraudRow {
  id: number
  user: string
  amount: number
  method: string
  reason: string
  created_at: string
  status: 'open' | 'confirmed' | 'cleared'
}

/** A25.14 — a referral-abuse cluster (one inviter, many fake invitees). */
export interface ReferralAbuseRow {
  id: number
  inviter: string
  invitees: number
  same_device: number
  reward: number
  status: 'open' | 'clawed_back' | 'cleared'
}

export function fetchDevices(params: FetchParams): Promise<FetchResult<DeviceRow>> {
  return safeList<DeviceRow>('/admin/antifraud/devices', 'devices', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.id} ${r.platform}`),
  )
}

export function fetchPaymentFraud(params: FetchParams): Promise<FetchResult<PaymentFraudRow>> {
  return safeList<PaymentFraudRow>('/admin/antifraud/payments', 'events', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.user} ${r.method} ${r.reason}`),
  )
}

/** POST /admin/antifraud/payments/:id/:action — confirm|clear (A25.12). */
export function resolvePaymentFraud(
  id: number,
  action: 'confirm' | 'clear',
): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/antifraud/payments/${id}/${action}`, {})
}

export function fetchReferralAbuse(params: FetchParams): Promise<FetchResult<ReferralAbuseRow>> {
  return safeList<ReferralAbuseRow>('/admin/antifraud/referral-abuse', 'clusters', params.signal).then(
    (rows) => toResult(rows, params, (r) => r.inviter),
  )
}

/** POST /admin/antifraud/referral-abuse/:id/clawback — reverse the reward (A25.14). */
export function clawbackReferral(id: number): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/antifraud/referral-abuse/${id}/clawback`, {})
}

// ============================================================ A25 iteration 2
// New screens (10). REAL endpoints reused where they exist; fetchers fail-soft to
// empty where the backend route is greenfield (ML models, scoring, backtest).

/** POST /admin/antifraud/rules {name,condition,action} — create a rule (A25.03, REAL). */
export function createFraudRule(
  name: string,
  condition: string,
  action: 'flag' | 'block' | 'review',
): Promise<{ ok: boolean; id: number }> {
  return api.post<{ ok: boolean; id: number }>('/admin/antifraud/rules', { name, condition, action })
}

/** A25.01 — one risk actor: events aggregated per user from `/antifraud/events`. */
export interface RiskActorRow {
  user: string
  events: number
  max_score: number
  open: number
  last_type: string
  last_seen: string
}

/**
 * A25.01 Risk dashboard — derived from the REAL `/admin/antifraud/events` feed:
 * groups scored events per user into a highest-risk-first leaderboard.
 */
export function fetchRiskDashboard(params: FetchParams): Promise<FetchResult<RiskActorRow>> {
  return safeList<SuspiciousEventRow>('/admin/antifraud/events', 'events', params.signal).then(
    (events) => {
      const byUser = new Map<string, RiskActorRow>()
      for (const e of events) {
        const key = e.user || '—'
        // events arrive newest-first (id DESC) → first hit keeps the latest type/seen.
        const cur =
          byUser.get(key) ??
          { user: key, events: 0, max_score: 0, open: 0, last_type: e.type, last_seen: e.created_at }
        cur.events += 1
        cur.max_score = Math.max(cur.max_score, Number(e.score) || 0)
        if (e.status === 'open') cur.open += 1
        byUser.set(key, cur)
      }
      const rows = [...byUser.values()].sort(
        (a, b) => b.max_score - a.max_score || b.events - a.events,
      )
      return toResult(rows, params, (r) => `${r.user} ${r.last_type}`)
    },
  )
}

/** A25.16 — a fraud-report line: totals per category, derived from REAL feeds. */
export interface FraudReportRow {
  id: string
  category: 'events' | 'payments' | 'referral'
  open: number
  resolved: number
  total: number
}

/**
 * A25.16 Fraud reports — aggregates the REAL events / payments / referral-abuse
 * feeds into an open-vs-resolved overview (no dedicated endpoint needed).
 */
export function fetchFraudReports(params: FetchParams): Promise<FetchResult<FraudReportRow>> {
  const s = params.signal
  return Promise.all([
    safeList<SuspiciousEventRow>('/admin/antifraud/events', 'events', s),
    safeList<PaymentFraudRow>('/admin/antifraud/payments', 'events', s),
    safeList<ReferralAbuseRow>('/admin/antifraud/referral-abuse', 'clusters', s),
  ]).then(([events, payments, clusters]) => {
    const row = (
      category: FraudReportRow['category'],
      total: number,
      open: number,
    ): FraudReportRow => ({ id: category, category, open, resolved: total - open, total })
    const rows: FraudReportRow[] = [
      row('events', events.length, events.filter((r) => r.status === 'open').length),
      row('payments', payments.length, payments.filter((r) => r.status === 'open').length),
      row('referral', clusters.length, clusters.filter((r) => r.status === 'open').length),
    ]
    return toResult(rows, params)
  })
}

/** A25.04 — a rule backtest result (fail-soft; `/antifraud/backtest` greenfield). */
export interface BacktestRow {
  id: number
  rule: string
  sample: number
  matched: number
  would_block: number
  precision: string
  period: string
}

export function fetchBacktest(params: FetchParams): Promise<FetchResult<BacktestRow>> {
  return safeList<BacktestRow>('/admin/antifraud/backtest', 'results', params.signal).then((rows) =>
    toResult(rows, params, (r) => r.rule),
  )
}

/** A25.05 — an ML risk model (fail-soft; `/antifraud/models` greenfield). */
export interface MlModelRow {
  id: string
  name: string
  kind: string
  version: string
  auc: number
  status: string
  updated_at: string
}

export function fetchMlModels(params: FetchParams): Promise<FetchResult<MlModelRow>> {
  return safeList<MlModelRow>('/admin/antifraud/models', 'models', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.name} ${r.kind}`),
  )
}

/** A25.06 — a scoring signal weight (fail-soft; `/antifraud/scoring` greenfield). */
export interface ScoringRow {
  id: string
  signal: string
  weight: number
  threshold: number
  enabled: boolean
}

export function fetchScoringConfig(params: FetchParams): Promise<FetchResult<ScoringRow>> {
  return safeList<ScoringRow>('/admin/antifraud/scoring', 'signals', params.signal).then((rows) =>
    toResult(rows, params, (r) => r.signal),
  )
}

/** Module-local RU strings (i18n HOT → keyга ko'chiriladi, REGISTER.md). */
export const FRAUD_STR = {
  events: 'Подозрительные события',
  rules: 'Правила анти-фрода',
  blacklist: 'Чёрные списки',
  user: 'Пользователь',
  type: 'Тип',
  score: 'Риск-балл',
  reason: 'Причина',
  created: 'Когда',
  status: 'Статус',
  name: 'Правило',
  condition: 'Условие',
  action: 'Действие',
  enabled: 'Вкл.',
  hits: 'Срабатываний',
  kind: 'Тип',
  value: 'Значение',
  added: 'Добавлен',
  block: 'Заблокировать',
  clear: 'Очистить (не фрод)',
  enable: 'Включить',
  disable: 'Выключить',
  remove: 'Удалить',
  blocked: 'Пользователь заблокирован',
  cleared: 'Событие очищено',
  toggled: 'Правило обновлено',
  removed: 'Запись удалена',
  failed: 'Не удалось выполнить',
  // A25.09 devices
  devices: 'Отпечатки устройств',
  device: 'Устройство',
  platform: 'Платформа',
  accounts: 'Аккаунтов',
  firstSeen: 'Первый раз',
  lastSeen: 'Последний раз',
  // A25.12 payment fraud
  paymentFraud: 'Платёжный фрод',
  amount: 'Сумма',
  method: 'Способ',
  confirm: 'Подтвердить фрод',
  confirmed: 'Отмечено как фрод',
  // A25.14 referral abuse
  referralAbuse: 'Злоупотребление рефералами',
  inviter: 'Пригласивший',
  invitees: 'Приглашённых',
  sameDevice: 'С одного устройства',
  reward: 'Награда',
  clawback: 'Отозвать награду',
  clawedBack: 'Награда отозвана',
  // A25.01 risk dashboard
  riskDashboard: 'Риск-дашборд',
  actor: 'Субъект',
  eventsCount: 'Событий',
  maxScore: 'Макс. риск',
  openCount: 'Открытых',
  lastType: 'Последний тип',
  // A25.03 rule create
  createRule: 'Создать правило',
  ruleCreateDesc: 'Новое правило анти-фрода',
  namePh: 'Название правила',
  conditionPh: 'Условие (напр. score > 80)',
  actionFlag: 'Пометить',
  actionBlock: 'Заблокировать',
  actionReview: 'На проверку',
  create: 'Создать',
  cancel: 'Отмена',
  ruleCreated: 'Правило создано',
  existingRules: 'Существующие правила',
  loadFailed: 'Не удалось загрузить',
  empty: 'Пусто',
  // A25.04 backtest
  backtest: 'Бэктест правил',
  rule: 'Правило',
  sample: 'Выборка',
  matched: 'Совпало',
  wouldBlock: 'Заблокировало бы',
  precision: 'Точность',
  period: 'Период',
  // A25.05 ML models
  mlModels: 'ML-модели риска',
  model: 'Модель',
  version: 'Версия',
  auc: 'AUC',
  updated: 'Обновлено',
  // A25.06 scoring
  scoring: 'Настройки скоринга',
  signal: 'Сигнал',
  weight: 'Вес',
  threshold: 'Порог',
  // A25.08 auto-actions
  autoActions: 'Автоматические меры',
  // A25.10 linked accounts
  linkedAccounts: 'Связанные аккаунты',
  fingerprint: 'Отпечаток',
  linked: 'Связано аккаунтов',
  flagged: 'Помечено',
  // A25.11 synthetic activity
  synthetic: 'Синтетическая активность',
  // A25.13 stars fraud
  starsFraud: 'Мошенничество со Stars',
  stars: 'Stars',
  // A25.16 reports
  reports: 'Отчёты о мошенничестве',
  category: 'Категория',
  resolved: 'Решено',
  total: 'Всего',
} as const
